/**
 * Lightweight pull-to-refresh hint shown at the top of a scroll container.
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

const DEFAULT_THRESHOLD = 72;

export interface PullToRefreshIndicatorProps {
  pullDistance: number;
  isPulling: boolean;
  isRefreshing?: boolean;
  threshold?: number;
  /** When true, pins indicator to the viewport top (site-wide handler). */
  fixed?: boolean;
}

export const PullToRefreshIndicator: React.FC<PullToRefreshIndicatorProps> = ({
  pullDistance,
  isPulling,
  isRefreshing = false,
  threshold = DEFAULT_THRESHOLD,
  fixed = false,
}) => {
  if (!isPulling && !isRefreshing && pullDistance === 0) return null;

  const ready = pullDistance >= threshold;
  const offsetY = Math.min(pullDistance * 0.35, 36);

  return (
    <div
      className={
        fixed
          ? 'pointer-events-none fixed left-0 right-0 top-0 z-[9999] flex justify-center pt-[env(safe-area-inset-top,0px)]'
          : 'pointer-events-none absolute left-0 right-0 top-0 z-20 flex justify-center'
      }
      style={{ transform: fixed ? undefined : `translateY(${offsetY}px)` }}
      aria-hidden
    >
      <div
        style={fixed ? { transform: `translateY(${offsetY}px)` } : undefined}
        className={cn(
          'flex items-center gap-2 rounded-full border border-[#3A3A3C] bg-[#1C1C1E]/95 px-3 py-1.5 text-[11px] font-medium text-[#8E8E93] shadow-lg transition-opacity',
          ready || isRefreshing ? 'text-white' : 'opacity-90',
        )}
      >
        <Loader2
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            isRefreshing ? 'animate-spin text-[#F33363]' : ready ? 'text-[#F33363]' : 'text-[#8E8E93]',
          )}
          style={
            !isRefreshing
              ? { transform: `rotate(${Math.min(pullDistance / threshold, 1) * 360}deg)` }
              : undefined
          }
        />
        <span>{isRefreshing ? 'Refreshing…' : ready ? 'Release to refresh' : 'Pull to refresh'}</span>
      </div>
    </div>
  );
};
